import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { Person } from "../lib/types";
import { Badge, EmptyState, Panel, Spinner } from "./ui";

/**
 * Who is in the household.
 *
 * Cards and loyalty accounts point at these rows, so deleting someone leaves
 * both behind unassigned rather than taking a ledger with them.
 */
export function PeopleTab({
  onError,
  onNotice,
}: {
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [draft, setDraft] = useState({ name: "", relationship: "" });

  const load = useCallback(async () => {
    try {
      setPeople((await api.get<{ people: Person[] }>("/people")).people);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load the household");
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  async function add() {
    if (!draft.name.trim()) return;
    try {
      await api.post("/people", { name: draft.name.trim(), relationship: draft.relationship.trim() || null });
      onNotice(`${draft.name.trim()} added.`);
      setDraft({ name: "", relationship: "" });
      void load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not add that person");
    }
  }

  async function save(person: Person, patch: Partial<Person>) {
    setPeople((current) => current?.map((p) => (p.id === person.id ? { ...p, ...patch } : p)) ?? null);
    try {
      await api.put(`/people/${person.id}`, { ...person, ...patch });
      if (patch.isSelf) void load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not save that change");
      void load();
    }
  }

  async function remove(person: Person) {
    const owns = [
      person.cardCount ? `${person.cardCount} card${person.cardCount === 1 ? "" : "s"}` : null,
      person.partnerCount ? `${person.partnerCount} loyalty account${person.partnerCount === 1 ? "" : "s"}` : null,
    ].filter(Boolean);
    const warning = owns.length
      ? ` ${owns.join(" and ")} will stay, but stop belonging to anyone.`
      : "";
    if (!confirm(`Remove ${person.name} from the household?${warning}`)) return;

    try {
      const r = await api.del<{ cardsUnassigned: number; accountsUnassigned: number }>(`/people/${person.id}`);
      onNotice(
        r.cardsUnassigned || r.accountsUnassigned
          ? `${person.name} removed. ${r.cardsUnassigned} card(s) and ${r.accountsUnassigned} account(s) are now unassigned.`
          : `${person.name} removed.`,
      );
      void load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not remove that person");
    }
  }

  if (people === null) return <Spinner />;

  return (
    <Panel
      title="Household"
      subtitle="Whose cards you manage. Points pool across everyone; transfers may not, so the planner needs to know who owns what."
    >
      <div className="mb-5 grid gap-3 rounded-lg bg-ink-50 p-4 sm:grid-cols-[2fr_2fr_auto] sm:items-end">
        <div>
          <label className="label" htmlFor="p-name">Name</label>
          <input
            id="p-name"
            className="input"
            placeholder="Priya"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <div>
          <label className="label" htmlFor="p-rel">Relationship</label>
          <input
            id="p-rel"
            className="input"
            placeholder="Spouse"
            value={draft.relationship}
            onChange={(e) => setDraft({ ...draft, relationship: e.target.value })}
          />
        </div>
        <button className="btn-primary" onClick={() => void add()} disabled={!draft.name.trim()}>Add</button>
      </div>

      {people.length === 0 ? (
        <EmptyState title="Nobody yet" hint="Add yourself first, then anyone whose cards you manage." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Relationship</th>
                <th className="py-2 pr-3 text-right">Cards</th>
                <th className="py-2 pr-3 text-right">Accounts</th>
                <th className="py-2 pr-3">That&apos;s me</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <tr key={person.id} className="border-b border-ink-100">
                  <td className="min-w-0 py-2 pr-3">
                    <input
                      className="input py-1 text-sm"
                      value={person.name}
                      aria-label={`Name of ${person.name}`}
                      onChange={(e) => setPeople((c) => c?.map((p) => (p.id === person.id ? { ...p, name: e.target.value } : p)) ?? null)}
                      onBlur={(e) => void save(person, { name: e.target.value })}
                    />
                  </td>
                  <td className="min-w-0 py-2 pr-3">
                    <input
                      className="input py-1 text-sm"
                      value={person.relationship ?? ""}
                      placeholder="—"
                      aria-label={`Relationship of ${person.name}`}
                      onChange={(e) => setPeople((c) => c?.map((p) => (p.id === person.id ? { ...p, relationship: e.target.value } : p)) ?? null)}
                      onBlur={(e) => void save(person, { relationship: e.target.value || null })}
                    />
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-ink-600">{person.cardCount ?? 0}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-ink-600">{person.partnerCount ?? 0}</td>
                  <td className="py-2 pr-3">
                    {person.isSelf ? (
                      <Badge tone="good">you</Badge>
                    ) : (
                      <button className="btn-ghost text-xs" onClick={() => void save(person, { isSelf: true })}>
                        Set
                      </button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button className="btn-ghost text-xs text-rose-700" onClick={() => void remove(person)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
